import {User} from "../../../api/accessControl/User.ts";
import {BackendApi} from "../../../api/api.ts";
import {useCallback, useEffect, useState} from "react";
import LoadingFrame from "../../../components/LoadingFrame.tsx";
import Markdown from "react-markdown";


function classNames(...classes) {
    return classes.filter(Boolean).join(' ')
}

function QuinnAvatar({className}) {
    return <img src="/quinn-green-bg.png" alt="" className='size-8'/>;
}

interface InsightProps {
    className?: string
    minimum?: boolean
    currentUser: User
    businessId: string
}

interface Insight {
    id: string;
    type: string;
    message: string;
    creator_type: string;
    creator_id: string;
    business_id: string;
    created_at: string;
    updated_at: string;
    is_read: boolean;
}

export default function PartnerClientInsights({className, minimum, currentUser, businessId}: InsightProps) {
    const [insights, setInsights] = useState<Insight[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    const fetchInsights = useCallback(async () => {
        try {
            const api = new BackendApi(currentUser.getToken())
            const response = await api.Partner__getClientInsights(businessId);
            let insights = [];
            response.insights.map(insight=>(
                insights.push({
                    id: insight.insight.id,
                    type: insight.insight.type,
                    message: insight.insight.message,
                    creator_type: insight.insight.creator_type,
                    creator_id: insight.insight.creator_id,
                    business_id: insight.insight.business_id,
                    created_at: insight.insight.created_at,
                    updated_at: insight.insight.updated_at,
                    is_read: insight.is_read
                })
            ))
            console.log(insights);
            setInsights(insights);
        } catch (error) {
            console.error('Error fetching insights:', error);
        }
    }, [currentUser]);

    const groupInsightsByDate = (insights: Insight[]) => {
        const groups: { [key: string]: Insight[] } = {};

        insights.forEach(insight => {
            const date = new Date(insight.created_at).toLocaleDateString();
            if (!groups[date]) {
                groups[date] = [];
            }
            groups[date].push(insight);
        });

        return Object.entries(groups).map(([date, insights]) => ({
            date,
            insights
        }));
    };

    const markInsightsAsRead = useCallback(async (insightIds: string[]) => {
        if (minimum) return;

        try {
            const api = new BackendApi(currentUser.getToken())
            await api.post('insights/mark-insights-read', {insight_ids: insightIds});
            setInsights(prevInsights =>
                prevInsights.map(insight =>
                    insightIds.includes(insight.id) ? {...insight, is_read: true} : insight
                )
            );
        } catch (error) {
            console.error('Error marking insights as read:', error);
        }
    }, [currentUser]);

    useEffect(() => {
        setIsLoading(true);
        fetchInsights().then(() => setIsLoading(false));
    }, [fetchInsights]);

    useEffect(() => {
        const unreadInsights = insights?.filter(insight => !insight.is_read);
        if (unreadInsights.length > 0) {
            markInsightsAsRead(unreadInsights?.map(insight => insight.id));
        }
    }, [insights, markInsightsAsRead]);

    const getInsights = () => {
        if (insights.length > 0) {
            return groupInsightsByDate(insights);
        }

        const defaultInsight = {
            id: '1',
            type: 'Welkom!',
            message: "Nog geen inzichten voor je bedrijf. We zijn hard bezig met het analyseren van de gegevens die je met ons hebt gedeeld.",
            creator_type: 'Quinn',
            creator_id: '',
            business_id: '',
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            is_read: true
        };

        return [{
            date: new Date().toLocaleDateString(),
            insights: [defaultInsight]
        }];
    };

    return (
        <>
            <div className={"lg:col-start-3  pb-8 rounded-md overflow-y-auto " + className}>
                <LoadingFrame isLoading={isLoading}>
                    <ul role="list" className="space-y-6">
                        {getInsights().map((group, groupIdx) => (
                            <div key={group.date}>
                                {/* Date divider */}
                                <div className="relative py-3">
                                    <div className="absolute inset-0 flex items-center" aria-hidden="true">
                                    </div>
                                    <div className="relative flex justify-center">
                                        <span
                                            className="bg-white px-3 text-sm font-medium text-gray-500 bg-gray-100 px-3 py-2 ring-1 ring-gray-200 rounded-xl">
                                            {group.date}
                                        </span>
                                    </div>
                                </div>

                                {/* Insights for this date */}
                                {group.insights.map((insight, insightIdx) => (
                                    <li key={insight.id} className="relative flex gap-x-4 pt-8 max-w-6xl mx-auto">
                                        <div
                                            className={classNames(
                                                (insightIdx === group.insights.length - 1) ? 'h-12' : '-bottom-6',
                                                'absolute left-0 top-0 flex w-6 justify-center',
                                                (insightIdx === 0) ? '-top-12' : ''
                                            )}
                                        >
                                        </div>

                                        <QuinnAvatar
                                            className="relative mt-3 h-6 w-6 flex-none rounded-full bg-gray-50"/>

                                        <div className={'w-full'}>
                                            <div
                                                className={`flex-auto rounded-md px-4 py-2 ring-1 ring-inset bg-gray-50 ${
                                                    insight.id ? 'ring-gray-100' : 'ring-blue-200 bg-blue-50'
                                                } cursor-pointer`}
                                            >
                                                <div className="mt-2 text-sm text-gray-700 markdown">
                                                    <Markdown>{insight.message}</Markdown></div>
                                                <div className="mt-2 flex justify-between gap-x-4">

                                                    {!insight.is_read && (
                                                        <span
                                                            className="inline-flex items-center rounded-full bg-red-100 px-2 py-1 text-xs font-medium text-red-700">
                                                    Nieuw
                                                </span>
                                                    )}
                                                </div>

                                            </div>
                                            <div
                                                className="py-0.5 text-xs leading-5 text-gray-500 flex items-center justify-between">
                                                <div>
                                                    <span className="font-medium text-gray-900">
                                                    Quinn
                                                </span>
                                                    {' • '}
                                                    {new Date(insight.created_at).toLocaleString([], {
                                                        hour: '2-digit',
                                                        minute: '2-digit'
                                                    })}
                                                </div>
                                                <div className="flex items-center gap-1 mt-2">
                                                    <h3 className="text-xs text-gray-900">{insight.type}</h3>
                                                    {/*{' • '}*/}
                                                    {/*<a href="#" className="text-blue-400">Waarom deze insight?</a>*/}
                                                </div>

                                            </div>
                                        </div>
                                    </li>
                                ))}
                            </div>
                        ))}
                    </ul>
                </LoadingFrame>
            </div>
        </>
    )
}
